import { JSX, useCallback, useEffect, useState } from "react";
import { Box, ToggleButtonGroup, Typography } from "@mui/material";
import { Calendar, List, Plus, AlertCircle } from "lucide-react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ListView from "./ListView";
import apiClient from "../../services/apiClient";
import "./CalendarStyles.css";
import LoadingScreen from "../../components/LoadingScreen";
import { formatDate } from "@fullcalendar/core";
import AppointmentDialog, { consultStatuses } from "./AppointmentEditForm";
import AppointmentViewDialog from "./AppointmentEditDialog";
import EmergencyConsults from "./EmergencyConsults";
import EmergencyDialog from "./AddEmergencyAppointment";
import { StyledToggleButton } from "./TodayAppointments";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SocketService from "../websocket/SocketService";

const AppointmentManagement = () => {
  const [appointments, setAppointments] = useState<any[]>([]);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"view" | "edit" | "add">("view");
  const [saving, setSaving] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchField, setSearchField] = useState<
    | "consultId"
    | "participantRef"
    | "consultStatus"
    | "dateRange"
    | "searchQuery"
  >("searchQuery");
  const [searchValue, setSearchValue] = useState<string>("");
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");

  type ViewType = "emergency" | "list" | "calendar" | "add" | "addEmergency";

  const [viewType, setViewType] = useState<ViewType>("list");

  const fetchAppointments = useCallback(
    async (forcedValue?: string, forcedField?: typeof searchField) => {
      try {
        setLoading(true);

        const value = forcedValue !== undefined ? forcedValue : searchValue;
        const field = forcedField || searchField;

        const params: any = {
          limit: itemsPerPage,
          page: currentPage,
          order_by: "id",
          dir: 1,
        };

        switch (field) {
          case "searchQuery":
            params.searchKey = value || "";
            break;
          case "consultId":
            params.consult_id = value || "";
            break;
          case "participantRef":
            params.participant_ref_number = value || "";
            break;
          case "consultStatus":
            params.consult_status = value || "";
            break;
          case "dateRange":
            if (fromDateValue) params.from_date = fromDateValue;
            if (toDateValue) params.to_date = toDateValue;
            break;
        }

        const queryString = new URLSearchParams(params).toString();
        const response: any = await apiClient(
          `v1/resource/consults?${queryString}`,
          { method: "GET" }
        );

        setAppointments(response.data.consults || []);
        setTotalCount(response.data.pagination?.total || 0);
      } catch (err: any) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [
      searchValue,
      searchField,
      currentPage,
      itemsPerPage,
      fromDateValue,
      toDateValue,
    ]
  );

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);
  useEffect(() => {
    const subscription = SocketService._listen<any>(
      "sourceCreated:veterinerian"
    ).subscribe((newConsult) => {
      setAppointments((prev) => {
        const exists = prev.some((item) => item.id === newConsult.id);
        if (exists) return prev;
        return [newConsult, ...prev];
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  // Listen to updates on each consult in the patientHistoriesList
  useEffect(() => {
    if (!appointments || appointments.length === 0) return;

    const subscriptions = appointments.map((item) =>
      SocketService.consultListener(item.id).subscribe(() => {
        fetchAppointments();
      })
    );

    return () => {
      subscriptions.forEach((sub) => sub.unsubscribe());
      appointments.forEach((item) => {
        SocketService.removeConsultListener(item.id);
      });
    };
  }, [appointments, fetchAppointments]);

  const handleEventClick = (info: any) => {
    setSelectedAppointment(info.event.extendedProps.appointmentDetails);
    setDialogMode("view");
    setDialogOpen(true);
  };

  const handleListViewClick = (appointment: any) => {
    setSelectedAppointment(appointment);
    setDialogMode("view");
    setDialogOpen(true);
  };

  const handleEditClick = (appointment: any, parentId: any) => {
    setSelectedAppointment(appointment);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleRefundClick = async (appointment: any) => {
    if (
      !appointment ||
      appointment.consult_status?.name.toLowerCase() !== "paid"
    ) {
      alert("Refund can only be triggered for paid appointments.");
      return;
    }

    try {
      await apiClient(`v1/resource/consults/${appointment.id}/refund`, {
        method: "POST",
      });
      setAppointments((prev) =>
        prev.map((appt) =>
          appt.id === appointment.id
            ? { ...appt, consult_status: { name: "refunded" } }
            : appt
        )
      );
      alert("Refund processed successfully.");
    } catch (error) {
      console.error("Refund error:", error);
      alert("Failed to process refund.");
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setCurrentPage(newPage + 1);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setItemsPerPage(newRowsPerPage);
    setPage(0);
    setCurrentPage(1);
  };

  const events = appointments.map((appointment: any) => ({
    id: appointment.id,
    title:
      appointment.participants
        .map((p: any) => p.participant_info?.name)
        .filter(Boolean)
        .join(", ") || "Appointment",
    start: appointment.scheduled_at,
    end: new Date(
      new Date(appointment.scheduled_at).getTime() + 30 * 60000
    ).toISOString(),
    backgroundColor:
      appointment.additional_info?.is_emergency === true ? "#dc2626" : "#43a047",
    borderColor:
      appointment.additional_info?.is_emergency === true ? "#b91c1c" : "#388e3c",
    extendedProps: { appointmentDetails: appointment },
  }));

  const viewButtons: { key: ViewType; label: string; icon: JSX.Element }[] = [
    { key: "list", label: "Appointment List", icon: <List size={18} /> },
    {
      key: "emergency",
      label: "Emergency Appointment",
      icon: <AlertCircle size={18} />,
    },
    { key: "calendar", label: "Calendar View", icon: <Calendar size={18} /> },
    { key: "add", label: "Add Appointment", icon: <Plus size={18} /> },
    {
      key: "addEmergency",
      label: "Add Emergency Appointment",
      icon: <Plus size={18} />,
    },
  ];

  if (loading && viewType !== "emergency")
    return <LoadingScreen message="Loading Appointments..." layout={false} />;

  if (error && viewType !== "emergency") {
    return <Typography color="error">Error: {error}</Typography>;
  }


  // Add new appointment
  const handleAddAppointment = async (data: any) => {
    try {
      setSaving(true);

      const consult = (await apiClient("v1/resource/consults", {
        method: "POST",
        body: data,
      })) as any;

      const response: any = await apiClient(
        `v1/resource/consults/${consult?.data?.consults?.id}`,
        { method: "GET" }
      );

      const newConsult = response?.data?.consult;
      if (newConsult) {
        SocketService.emitConsultCreated(newConsult);
      }

      await fetchAppointments();
      setDialogOpen(false);
      setViewType("list");
    } catch (err) {
      console.error("Error adding appointment:", err);
      alert("Failed to add appointment");
    } finally {
      setSaving(false);
    }
  };

  // Edit existing appointment
  const handleEditAppointment = async (id: number, data: any) => {
    try {
      setSaving(true);

      // Update the appointment
      await apiClient(`v1/resource/consults/${id}`, {
        method: "PATCH",
        body: data,
      });

      // Fetch the updated consult
      const response: any = await apiClient(`v1/resource/consults/${id}`, {
        method: "GET",
      });

      const updatedConsult = response?.data?.consult;
      if (updatedConsult) {
        SocketService.emitConsultCreated(updatedConsult);
      }

      await fetchAppointments();
      setDialogOpen(false);
      setViewType("list");
    } catch (err) {
      console.error("Error editing appointment:", err);
      alert("Failed to edit appointment");
    } finally {
      setSaving(false);
    }
  };

  function getEventTextColor(event: any) {
    const bg = event.backgroundColor || "#43a047";
    const c = bg.substring(1);
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 180 ? "#000000" : "#ffffff";
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          {/* Optional: Uncomment to show heading */}
          {/* <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976d2' }}>
                        {viewHeadings[viewType]}
                    </Typography> */}
        </Box>

        <div className="flex items-center space-x-3">
          <ToggleButtonGroup
            value={viewType}
            exclusive
            onChange={(_e, newView) => {
              if (!newView) return;

              // If switching away from list view, reset filters
              if (newView !== "list") {
                setSearchValue("");
                setSearchField("searchQuery");
                setFromDateValue("");
                setToDateValue("");
                fetchAppointments("", "searchQuery");
              }

              setViewType(newView as ViewType);

              if (newView === "add" || newView === "addEmergency") {
                setDialogMode("add");
                setDialogOpen(true);
              }
            }}
            sx={{ backgroundColor: "#f5f7fa", borderRadius: 12, p: 0.5 }}
          >
            {viewButtons.map(({ key, label, icon }) => (
              <StyledToggleButton key={key} value={key}>
                <span style={{ marginLeft: 6 }}>{label}</span>
              </StyledToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
      </Box>

      {/* View Rendering */}
      {viewType === "calendar" && (
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          buttonText={{
            today: "Today",
            month: "Month",
            week: "Week",
            day: "Day",
          }}
          selectable
          editable
          droppable
          initialDate={new Date()}
          events={events}
          eventClick={handleEventClick}
          dateClick={(info) => console.log("Date clicked:", info.dateStr)}
          eventAdd={(info) => console.log("Event added:", info.event)}
          eventChange={(info) => console.log("Event changed:", info.event)}
          eventRemove={(info) => console.log("Event removed:", info.event)}
          eventContent={(arg) => {
            const formattedTime = formatDate(arg.event.start!, {
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
            return (
              <div style={{ color: getEventTextColor(arg.event) }}>
                <b>{formattedTime}</b> {arg.event.title}
              </div>
            );
          }}
        />
      )}

      {viewType === "emergency" && (
        <EmergencyConsults
          handleListViewClick={handleListViewClick}
          handleEditClick={handleEditClick}
          fetchAppointments={fetchAppointments}
        />
      )}

      {viewType === "list" && (
        <>
          <div className="flex items-center gap-2 mb-2 mt-6 flex-wrap">
            <select
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value as any);
                setSearchValue(""); // reset value when field changes
                setFromDateValue(""); // reset dates
                setToDateValue("");
              }}
              className="border px-2 py-1 rounded"
            >
              <option value="searchQuery">Name / Mobile / Service</option>
              <option value="consultId">Consult ID</option>
              <option value="participantRef">Participant Ref</option>
              <option value="consultStatus">Consult Status</option>
              <option value="dateRange">Date Range</option>
            </select>

            {searchField === "dateRange" ? (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={fromDateValue ? new Date(fromDateValue) : null}
                  onChange={(date) =>
                    setFromDateValue(
                      date ? date.toISOString().split("T")[0] : ""
                    )
                  }
                  slotProps={{
                    textField: {
                      className: "border px-2 py-1 rounded",
                      size: "small",
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDateValue ? new Date(toDateValue) : null}
                  onChange={(date) =>
                    setToDateValue(date ? date.toISOString().split("T")[0] : "")
                  }
                  slotProps={{
                    textField: {
                      className: "border px-2 py-1 rounded",
                      size: "small",
                    },
                  }}
                />
              </LocalizationProvider>
            ) : searchField === "consultStatus" ? (
              <select
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border px-2 py-1 rounded"
              >
                <option value="">All Status</option>
                {consultStatuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder={`Search by ${searchField}`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border px-2 py-1 rounded flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchAppointments();
                }}
              />
            )}

            {/* Search button */}
            <button
              onClick={() => fetchAppointments()}
              className="bg-blue-600 text-white px-3 py-1 rounded"
              disabled={
                searchField === "dateRange" && (!fromDateValue || !toDateValue)
              }
            >
              Search
            </button>

            <button
              onClick={() => {
                setSearchValue("");
                setSearchField("searchQuery");
                setFromDateValue("");
                setToDateValue("");
                setPage(0);
                setCurrentPage(1);
                fetchAppointments("", "searchQuery");
              }}
              className="bg-red-400 text-white px-3 py-1 rounded"
            >
              Clear
            </button>
          </div>

          <ListView
            appointments={appointments}
            page={page}
            rowsPerPage={rowsPerPage}
            loading={loading}
            error={error}
            totalCount={totalCount}
            handleListViewClick={handleListViewClick}
            handleEditClick={handleEditClick}
            handleRefundClick={handleRefundClick}
            handlePageChange={handlePageChange}
            handleRowsPerPageChange={handleRowsPerPageChange}
            fetchAppointments={fetchAppointments}
          />
        </>
      )}

      {/* Add Appointment Dialog */}
      {dialogOpen && dialogMode === "add" && viewType === "add" && (
        <AppointmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          initialValues={undefined}
          onSave={handleAddAppointment}
          isEdit={false}
          saving={saving}
        />
      )}

      {/* Add Emergency Appointment Dialog */}
      {dialogOpen && dialogMode === "add" && viewType === "addEmergency" && (
        <EmergencyDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleAddAppointment}
          saving={saving}
        />
      )}

      {/* Edit Appointment Dialog */}
      {dialogOpen && dialogMode === "edit" && (
        <AppointmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          initialValues={selectedAppointment}
          onSave={(data) => handleEditAppointment(selectedAppointment.id, data)}
          isEdit={true}
          saving={saving}
        />
      )}

      {/* View Appointment Dialog */}
      {dialogOpen && dialogMode === "view" && (
        <AppointmentViewDialog
          open={dialogOpen}
          appointment={selectedAppointment}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default AppointmentManagement;
